import { useMemo } from "react";
import { useDispatch } from "react-redux";

import { IgButton } from "@intelligems/ig-theme";
import {
  CouponType,
  CreateBillingRequestBodyType,
  DowngradeBillingRequestBodyType,
  LivePriceDto,
  StripeSubscriptionSummaryDto,
  UpgradeBillingRequestBodyType,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { useAdjustBilling } from "@src/api/stripe/adjust-billing";
import { setDialog } from "@src/store/slices/global";
import { useIsMutating } from "@tanstack/react-query";

interface ButtonRowProps {
  price: LivePriceDto;
  level: number;
  currentPlanLevel: number;
  coupon: CouponType | null;
  upcomingChange: {
    variant: string;
    word: string;
    date: string;
  } | null;
}

export const ButtonRow = ({
  price,
  level,
  currentPlanLevel,
  upcomingChange,
  coupon,
}: ButtonRowProps) => {
  const orgSummary = useGetOrganizationSummary();
  const adjustBilling = useAdjustBilling();
  const dispatch = useDispatch();
  const isMutating = useIsMutating({ mutationKey: ["adjustBilling"] });
  const { hasActiveSub, isStripe } = useMemo(() => {
    if (orgSummary.isSuccess) {
      const hasActiveSub = (
        orgSummary.data.org.stripeSubscriptionSummaries || []
      ).some((sl) => sl.status === "active" || sl.status === "trialing");

      const isStripe =
        orgSummary.data?.org.stripeSubscriptionSummaries.some(
          (s) => s.billingProvider === "stripe"
        ) ||
        orgSummary.data?.org.organizationSubscriptions.some(
          (os) => os.billingProvider === "stripe" && os.status === "active"
        );

      return { hasActiveSub, isStripe };
    }
    return { hasActiveSbu: false, isStripe: false };
  }, [orgSummary.isSuccess, orgSummary.data]);

  const isUpgrade = level >= currentPlanLevel;
  const isDowngrade = level < currentPlanLevel;

  const isCurrentPlan = orgSummary.data.org.stripeSubscriptionSummaries.some(
    (s) =>
      s.stripePriceId === price.id && ["active", "trialing"].includes(s.status)
  );

  const onButtonClick = () => {
    if (!orgSummary.isSuccess) {
      return;
    }

    const activeSub: StripeSubscriptionSummaryDto | undefined =
      orgSummary.data.org.stripeSubscriptionSummaries.find((sl) =>
        ["active", "trialing"].includes(sl.status)
      );

    if (activeSub && isStripe) {
      dispatch(
        setDialog({
          dialog: "modifyStripeSubscription",
          open: true,
        })
      );
      return;
    }

    if (
      price.product.name.includes("Profit Optimization") ||
      price.product.name.includes("Intelligems Blue")
    ) {
      dispatch(
        setDialog({
          dialog: "upgradeToProfitOptimization",
          open: true,
          props: {
            couponId: coupon ? coupon.id : undefined,
            priceIds: price.priceIds,
            action: activeSub ? "upgrade" : "create",
            stripeSubscriptionLinkId: activeSub ? activeSub.id : undefined,
          },
        })
      );
    } else if (activeSub) {
      const upgradePayload: UpgradeBillingRequestBodyType = {
        version: price.product.version || "v2",
        couponId: coupon ? coupon.id : undefined,
        stripeSubscriptionLinkId: activeSub.id,
        action: "upgrade",
        priceIds: price.priceIds,
      };

      const downgradePayload: DowngradeBillingRequestBodyType = {
        version: price.product.version || "v2",
        couponId: coupon ? coupon.id : undefined,
        stripeSubscriptionLinkId: activeSub.id,
        action: "downgrade",
        priceIds: price.priceIds,
      };

      adjustBilling.mutate({
        pathParams: undefined,
        queryParams: undefined,
        body: isUpgrade ? upgradePayload : downgradePayload,
      });
    } else {
      const createPayload: CreateBillingRequestBodyType = {
        version: price.product.version,
        couponId: coupon ? coupon.id : undefined,
        action: "create",
        priceIds: price.priceIds,
      };

      adjustBilling.mutate({
        pathParams: undefined,
        queryParams: undefined,
        body: createPayload,
      });
    }
  };

  const text = useMemo(() => {
    if (upcomingChange) {
      return `${upcomingChange.word} ${upcomingChange.date}`;
    }

    if (hasActiveSub && isCurrentPlan) {
      return "Current Plan";
    }

    if (!price.orderCountWithinLimit) {
      return "Unavailable";
    }

    if (hasActiveSub) {
      if (isCurrentPlan) {
        return "Current Plan";
      }

      if (orgSummary.isSuccess && orgSummary.data.org.agencies.length) {
        return null;
      }

      if (isStripe) {
        return "Contact Sales";
      }

      if (isUpgrade) {
        return "Upgrade Plan";
      }

      if (isDowngrade) {
        return "Downgrade Plan";
      }
    }

    return "Get Started";
  }, [
    upcomingChange,
    hasActiveSub,
    isUpgrade,
    isCurrentPlan,
    isDowngrade,
    orgSummary.isSuccess,
    orgSummary.data,
  ]);

  if (!text) {
    return null;
  }

  return (
    <Grid container item sx={{ marginTop: "18px" }}>
      <Grid item xs={12}>
        <IgButton
          loading={isMutating > 0}
          fullWidth={true}
          variant={"contained"}
          disabled={
            !!upcomingChange ||
            (hasActiveSub && isCurrentPlan) ||
            !price.orderCountWithinLimit
          }
          onClick={onButtonClick}
        >
          {text}
        </IgButton>
      </Grid>
    </Grid>
  );
};
